import React from 'react'
import { Eye, MoreVertical } from 'lucide-react'
import { cn } from 'components/UI/cn'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { useModalDepth } from 'components/UI/Modal/ModalPrimitive'
import { getDropdownZIndex } from 'styles/z-index'
import { EmailThread } from 'models/emailIntegration'
import { ComposeMode } from './ComposePanel'
import {
  EmailDealActionCallbacks,
  buildActionContext,
  useEmailThreadActions,
  EmailAction,
} from './emailActions'
import { EmailActionMenuItems } from './EmailActionMenuItems'

interface InboxRowMenuProps extends EmailDealActionCallbacks {
  thread: EmailThread
  isSelected: boolean
  isOpen?: boolean
  onView: (thread: EmailThread) => void
  onCompose: (thread: EmailThread, mode: ComposeMode) => void
  assignDealSubContent?: React.ReactNode
  onOpenChange?: (open: boolean) => void
}

/** 3-dot menu shown on inbox row hover, overlaying the timestamp column. */
export function InboxRowMenu({
  thread,
  isSelected,
  isOpen,
  onView,
  onCompose,
  assignDealSubContent,
  onOpenChange,
  ...dealCallbacks
}: InboxRowMenuProps) {
  const modalDepth = useModalDepth()
  const zIndex = React.useMemo(
    () => getDropdownZIndex(modalDepth),
    [modalDepth]
  )

  const handleCompose = React.useCallback(
    (mode: ComposeMode) => onCompose(thread, mode),
    [onCompose, thread]
  )

  const threadActions = useEmailThreadActions(
    buildActionContext(
      dealCallbacks,
      handleCompose,
      assignDealSubContent,
      thread.is_hidden,
      thread.is_own_thread
    )
  )

  // Prepend a "View" action before the shared actions
  const viewAction: EmailAction = React.useMemo(
    () => ({
      label: 'View',
      icon: Eye,
      onClick: () => onView(thread),
      disabled: false,
      hasSeparatorAfter: true,
    }),
    [onView, thread]
  )

  // Row menu only shows actionable items — hide disabled actions.
  // Strip separators that would end up at the bottom after filtering.
  const actions = React.useMemo(() => {
    const filtered = [viewAction, ...threadActions.filter((a) => !a.disabled)]
    // Remove separator from last item since nothing follows it
    if (filtered.length > 0) {
      const last = filtered[filtered.length - 1]
      if (last.hasSeparatorAfter) {
        filtered[filtered.length - 1] = { ...last, hasSeparatorAfter: false }
      }
    }
    return filtered
  }, [viewAction, threadActions])

  return (
    <DropdownMenu onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'flex items-center justify-center size-7 rounded transition-colors',
            isSelected
              ? cn('hover:bg-black/15', isOpen && 'bg-black/15')
              : cn('hover:bg-surface-disabled', isOpen && 'bg-surface-disabled')
          )}
        >
          <MoreVertical
            className={cn(
              'size-4',
              isSelected || isOpen ? 'text-fg' : 'text-fg-secondary'
            )}
          />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        style={{ minWidth: 200, zIndex }}
        className="bg-surface border-line-focus"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <EmailActionMenuItems actions={actions} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
